"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import "mapbox-gl/dist/mapbox-gl.css";
import { distanceKm, formatDistance } from "@/components/guide-public/guide-map-clusters";
import {
  attachClusters,
  attachSpotlight,
  calloutElement,
  MAP_STYLES,
  REGION_CHANGED_EVENT,
  REGION_PICK_EVENT,
  type ClusterEngine,
  type EngineMarker,
  type MapStyleKey,
  type RegionPickDetail,
  type Spotlight,
} from "@/components/guide-public/guide-map-engine";
import { DistinctionMark } from "@/components/guide-public/guide-ornaments";
import { GuidePhoto } from "@/components/guide-public/guide-parts";
import { GuideSaveButton } from "@/components/guide-public/guide-saved";
import { eventInWindow, type EventWindow } from "@/lib/virtualspace/guide/guide-dates";
import { openLabel, openState, type Hours } from "@/lib/virtualspace/guide/guide-open-hours";

const TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "";
const REGION_MEMORY_KEY = "localized:region";

export type ExplorerVenue = {
  slug: string;
  name: string;
  category: string;
  categorySlug: string;
  region: string;
  regionSlug: string;
  distinction: number;
  distinctionName: string;
  hasTour: boolean;
  brief: string | null;
  bestFor?: string | null;
  priceLevel?: number | null;
  coverUrl: string | null;
  hours?: Hours;
  latitude: number | null;
  longitude: number | null;
};

export type ExplorerRegion = {
  slug: string;
  label: string;
  parentSlug: string | null;
  bounds: [[number, number], [number, number]];
};

/** An event on the map: its own pin, else its venue's; unpinned ones are listed under their region. */
export type ExplorerEvent = {
  id: string;
  title: string;
  dates: string;
  mon: string;
  day: string;
  where: string | null;
  region: string;
  regionSlug: string | null;
  onNow: boolean;
  startsAt?: string;
  endsAt?: string | null;
  href: string | null;
  external: boolean;
  latitude: number | null;
  longitude: number | null;
};

export type ExplorerNews = {
  id: string;
  title: string;
  month: string;
  source: string | null;
  region: string;
  regionSlug: string | null;
  href: string | null;
  external: boolean;
  latitude: number | null;
  longitude: number | null;
};

export type ExplorerLayer = "places" | "events" | "news";
export type ExplorerCategory = { slug: string; label: string; group?: "see" | "do" | "eat" };
export type ExplorerSort = "default" | "name" | "distinction" | "nearest";

/** The filters a page address can carry (?open=1&window=week&price=1,2&min=1&tour=1&sort=name). */
export type ExplorerInitialFilters = { open?: string; window?: string; price?: string; min?: string; tour?: string; sort?: string };

type Copy = {
  all: string;
  allRegions: string;
  region: string;
  category: string;
  places: string;
  place: string;
  layerAll: string;
  layerPlaces: string;
  layerEvents: string;
  layerNews: string;
  events: string;
  event: string;
  stories: string;
  story: string;
  onNow: string;
  acrossEgypt: string;
  acrossEgyptNote: string;
  empty: string;
  emptyEvents: string;
  emptyNews: string;
  mapUnavailable: string;
  tourShort: string;
  viewVenue: string;
  officialSite: string;
  readStory: string;
  directions: string;
  nearestFirst: string;
  recentre: string;
  legendCluster: string;
  legendHere: string;
  legendHint: string;
  clusterOf: string;
  youAreHere: string;
  openNow: string;
  closedNow: string;
  filterOpenNow: string;
  eventWindows: Record<EventWindow, string>;
  styleStreets: string;
  styleSatellite: string;
  shareView: string;
  linkCopied: string;
  loadingMap: string;
  groups: Record<"see" | "do" | "eat", string>;
  filters: {
    price: string;
    distinction: string;
    tour: string;
    sort: string;
    sortDefault: string;
    sortName: string;
    sortDistinction: string;
    sortNearest: string;
    clear: string;
    showMap: string;
    showList: string;
    any: string;
    more: string;
  };
  saved: { save: string; saved: string; unsave: string };
  priceBands: readonly string[];
};

type MapboxLayer = { id: string; type: string };
type MapboxMap = {
  fitBounds: (b: [[number, number], [number, number]], o?: Record<string, unknown>) => void;
  setStyle: (style: string) => void;
  remove: () => void;
  resize: () => void;
};
type StylableMap = MapboxMap & {
  on: (event: string, fn: () => void) => void;
  getStyle: () => { layers?: MapboxLayer[] };
  setPaintProperty: (layer: string, prop: string, value: string) => void;
};
type Popup = { remove: () => void };
type PopupClass = new (o: Record<string, unknown>) => {
  setLngLat: (l: [number, number]) => { setDOMContent: (el: HTMLElement) => { addTo: (m: unknown) => Popup } };
};

/**
 * Repaint Mapbox's light style in the Guide's palette: desert sand land,
 * Nile-blue water, ivory roads and ink labels. Layer names differ between
 * style versions, so it matches by type and name and skips what it can't set.
 */
export function paintEgypt(map: StylableMap) {
  for (const layer of map.getStyle().layers ?? []) {
    const { id, type } = layer;
    const set = (prop: string, value: string) => {
      try {
        map.setPaintProperty(id, prop, value);
      } catch {
        // Not every layer of a type takes every property.
      }
    };
    if (type === "background") set("background-color", "#e9dfcc");
    else if (type === "fill" && /water/.test(id)) set("fill-color", "#9fb8c6");
    else if (type === "line" && /water/.test(id)) set("line-color", "#7d9db1");
    else if (type === "fill" && /building/.test(id)) set("fill-color", "#dccba9");
    else if (type === "fill" && /land|park|national|pitch|sand/.test(id)) set("fill-color", "#e4d6bb");
    else if (type === "line" && /road|bridge|tunnel|street/.test(id)) set("line-color", "#fffdf8");
    else if (type === "line" && /admin|boundary/.test(id)) set("line-color", "#b8a886");
    else if (type === "symbol") {
      set("text-color", "#3e362e");
      set("text-halo-color", "#f5f0e6");
    }
  }
}

/** "Greater Cairo" first: the launch edition's centre of gravity. */
const START_REGION = "greater-cairo";

function regionAndDescendants(slug: string, regions: ExplorerRegion[]): Set<string> {
  const out = new Set([slug]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const r of regions) {
      if (r.parentSlug && out.has(r.parentSlug) && !out.has(r.slug)) {
        out.add(r.slug);
        grew = true;
      }
    }
  }
  return out;
}

const inBounds = (b: ExplorerRegion["bounds"], lat: number, lng: number) =>
  lng >= b[0][0] && lng <= b[1][0] && lat >= b[0][1] && lat <= b[1][1];

type Pinned = { latitude: number | null; longitude: number | null };
type Point = { latitude: number; longitude: number };
const isPinned = (p: Pinned): p is Point => p.latitude !== null && p.longitude !== null;

type MapItem = { key: string; kind: ExplorerLayer; label: string; glyph: string; now?: boolean; latitude: number; longitude: number };

const ITEM_STYLE: React.CSSProperties = {
  padding: 10,
  display: "flex",
  gap: 14,
  alignItems: "center",
  background: "var(--eg-card)",
  borderRadius: 4,
};

const directionsHref = (p: Point) => `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`;

function rememberRegion(slug: string) {
  try {
    if (slug === "all") window.localStorage.removeItem(REGION_MEMORY_KEY);
    else window.localStorage.setItem(REGION_MEMORY_KEY, slug);
  } catch {
    // Private windows and blocked storage: the map simply forgets.
  }
}

const PRICE_LEVELS = [1, 2, 3, 4] as const;
const SORTS: ExplorerSort[] = ["default", "name", "distinction", "nearest"];

/** Price levels from "1,2"; an empty set means any. */
function parsePrices(value: string | undefined): Set<number> {
  return new Set((value ?? "").split(",").map(Number).filter((n) => PRICE_LEVELS.includes(n as 1 | 2 | 3 | 4)));
}

/**
 * The Guide's map hub: places, what's on and news on one map of Egypt. A
 * layer switch (everything, or one kind), region chips and a picker that
 * zoom the map and spotlight the region, category chips grouped as See / Do
 * / Eat, open-now, price, distinction and 3D-tour filters, a sort, and date
 * chips for events. Pins that would overlap gather into clusters; a pin
 * opens a callout and finds its card; a card opens the venue, the event or
 * the story's source. Nationwide items with no single place sit under
 * "Across Egypt". The visitor can switch to satellite, ask where they are
 * and sort by distance, save places, and share the view (the address keeps
 * every filter). On a phone the map and the list take turns. The drawn
 * Egypt above sends its region picks here. Without WebGL or a token the
 * lists alone remain.
 */
export function GuideExplorer({
  venues,
  events = [],
  news = [],
  regions,
  categories,
  copy,
  venueHref,
  initialRegion = "all",
  initialLayer,
  initialCategory,
  initialFilters = {},
  layers = ["places", "events", "news"],
  countrySlug = "egypt",
  lockRegion = false,
  lockCategory = false,
}: {
  venues: ExplorerVenue[];
  events?: ExplorerEvent[];
  news?: ExplorerNews[];
  regions: ExplorerRegion[];
  categories: ExplorerCategory[];
  copy: Copy;
  venueHref: Record<string, string>;
  /** A region slug from the page URL (?region=), chosen from the drawn map. */
  initialRegion?: string;
  /** A layer from the page URL (?show=places|events|news). */
  initialLayer?: string;
  /** A category slug from the page URL (?category=). */
  initialCategory?: string;
  /** The other filters from the page URL. */
  initialFilters?: ExplorerInitialFilters;
  /** Which kinds this map offers; one kind hides the switch (What's on, Journal). */
  layers?: ExplorerLayer[];
  /** The country region: items filed here (or nowhere) are nationwide. */
  countrySlug?: string;
  /** A region page: the region is fixed, so its picker and chips are hidden. */
  lockRegion?: boolean;
  /** A category page: the category is fixed. */
  lockCategory?: boolean;
}) {
  const [layer, setLayer] = useState<ExplorerLayer | "all">(
    layers.length === 1 ? layers[0] : (layers as string[]).includes(initialLayer ?? "") ? (initialLayer as ExplorerLayer) : "all",
  );
  const [category, setCategory] = useState(categories.some((c) => c.slug === initialCategory) ? (initialCategory as string) : "all");
  const [region, setRegion] = useState(regions.some((r) => r.slug === initialRegion) ? initialRegion : "all");
  const [openOnly, setOpenOnly] = useState(initialFilters.open === "1");
  const [window_, setWindow] = useState<EventWindow>(
    (["all", "now", "week", "month"] as EventWindow[]).includes(initialFilters.window as EventWindow) ? (initialFilters.window as EventWindow) : "all",
  );
  const [prices, setPrices] = useState<Set<number>>(() => parsePrices(initialFilters.price));
  const [minDistinction, setMinDistinction] = useState(() => {
    const n = Number(initialFilters.min);
    return Number.isInteger(n) && n >= 0 && n <= 3 ? n : 0;
  });
  const [tourOnly, setTourOnly] = useState(initialFilters.tour === "1");
  const [sort, setSort] = useState<ExplorerSort>(SORTS.includes(initialFilters.sort as ExplorerSort) ? (initialFilters.sort as ExplorerSort) : "default");
  const [moreFilters, setMoreFilters] = useState(Boolean(initialFilters.price || (initialFilters.min && initialFilters.min !== "0") || initialFilters.tour));
  const [mobileView, setMobileView] = useState<"map" | "list">("list");
  const [selected, setSelected] = useState<string | null>(null);
  const [here, setHere] = useState<Point | null>(null);
  const [style, setStyle] = useState<MapStyleKey>("streets");
  const [mapFailed, setMapFailed] = useState(!TOKEN);
  const [mapReady, setMapReady] = useState(false);
  const [shared, setShared] = useState(false);
  // The page's language, from the Localized wrapper (set after mount, so the server and client agree).
  const [uiLang, setUiLang] = useState<"en" | "ar">("en");
  const [now] = useState(() => new Date());
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const popupClassRef = useRef<PopupClass | null>(null);
  const popupRef = useRef<Popup | null>(null);
  const markersRef = useRef<Map<string, EngineMarker>>(new Map());
  const clustersRef = useRef<ClusterEngine | null>(null);
  const spotlightRef = useRef<Spotlight | null>(null);
  const cardRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const mounted = useRef(false);

  const nearestFirst = sort === "nearest" && here !== null;
  const shows = (kind: ExplorerLayer) => layers.includes(kind) && (layer === "all" || layer === kind);
  const regionFilter = useMemo(
    () => (region === "all" ? null : regionAndDescendants(region, regions)),
    [region, regions],
  );
  const regionBounds = regions.find((r) => r.slug === region)?.bounds;
  const national = (slug: string | null) => !slug || slug === countrySlug;

  // An event or story belongs to the chosen region by its filing, or, when
  // filed nationally, by where its pin falls.
  const inRegion = <T extends Pinned & { regionSlug: string | null }>(item: T) => {
    if (regionFilter === null) return true;
    if (item.regionSlug && regionFilter.has(item.regionSlug)) return true;
    return national(item.regionSlug) && isPinned(item) && regionBounds !== undefined && inBounds(regionBounds, item.latitude, item.longitude);
  };

  // Nearest first, when the visitor has asked where they are; unpinned items keep their order at the end.
  const byDistance = <T extends Pinned>(items: T[]): T[] => {
    if (!here || !nearestFirst) return items;
    const km = (i: T) => (isPinned(i) ? distanceKm(here, i) : Number.POSITIVE_INFINITY);
    return [...items].sort((a, b) => km(a) - km(b));
  };

  const openOf = useMemo(() => new Map(venues.map((v) => [v.slug, openState(v.hours, now)])), [venues, now]);
  const passesVenueFilters = (v: ExplorerVenue) =>
    (category === "all" || v.categorySlug === category) &&
    (regionFilter === null || regionFilter.has(v.regionSlug)) &&
    (!openOnly || (openOf.get(v.slug)?.known === true && (openOf.get(v.slug) as { open?: boolean }).open === true)) &&
    (prices.size === 0 || (v.priceLevel !== null && v.priceLevel !== undefined && prices.has(v.priceLevel))) &&
    v.distinction >= minDistinction &&
    (!tourOnly || v.hasTour);
  const visibleVenues = useMemo(
    () => {
      const list = venues.filter(passesVenueFilters);
      if (sort === "name") return [...list].sort((a, b) => a.name.localeCompare(b.name, uiLang === "ar" ? "ar" : "en"));
      if (sort === "distinction") return [...list].sort((a, b) => b.distinction - a.distinction || a.name.localeCompare(b.name));
      return byDistance(list);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [venues, category, regionFilter, here, sort, openOnly, openOf, prices, minDistinction, tourOnly, uiLang],
  );
  const inWindow = (e: ExplorerEvent) => !e.startsAt || eventInWindow({ startsAt: e.startsAt, endsAt: e.endsAt ?? null }, window_, now);
  const local = <T extends Pinned & { regionSlug: string | null }>(items: T[]) =>
    byDistance(items.filter((i) => !(national(i.regionSlug) && !isPinned(i)) && inRegion(i)));
  const across = <T extends Pinned & { regionSlug: string | null }>(items: T[]) =>
    items.filter((i) => national(i.regionSlug) && !isPinned(i));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleEvents = useMemo(() => local(events.filter(inWindow)), [events, regionFilter, regionBounds, here, nearestFirst, window_]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleNews = useMemo(() => local(news), [news, regionFilter, regionBounds, here, nearestFirst]);
  const nationalEvents = useMemo(() => across(events.filter(inWindow)), [events, window_]); // eslint-disable-line react-hooks/exhaustive-deps
  const nationalNews = useMemo(() => across(news), [news]); // eslint-disable-line react-hooks/exhaustive-deps

  const mapItems = useMemo<MapItem[]>(() => {
    const out: MapItem[] = [];
    for (const v of venues) {
      if (isPinned(v)) out.push({ key: `p:${v.slug}`, kind: "places", label: `${v.name}, ${v.distinctionName}`, glyph: v.distinction > 0 ? "☥".repeat(v.distinction) : "✦", latitude: v.latitude, longitude: v.longitude });
    }
    for (const e of events) {
      if (isPinned(e)) out.push({ key: `e:${e.id}`, kind: "events", label: `${e.title}, ${e.dates}`, glyph: e.day, now: e.onNow, latitude: e.latitude, longitude: e.longitude });
    }
    for (const n of news) {
      if (isPinned(n)) out.push({ key: `n:${n.id}`, kind: "news", label: `${n.title}, ${n.month}`, glyph: "", latitude: n.latitude, longitude: n.longitude });
    }
    return out;
  }, [venues, events, news]);
  const itemByKey = useMemo(() => new Map(mapItems.map((i) => [i.key, i])), [mapItems]);

  const shownKeys = useMemo(() => {
    const keys = new Set<string>();
    if (shows("places")) for (const v of visibleVenues) keys.add(`p:${v.slug}`);
    if (shows("events")) for (const e of visibleEvents) keys.add(`e:${e.id}`);
    if (shows("news")) for (const n of visibleNews) keys.add(`n:${n.id}`);
    return keys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleVenues, visibleEvents, visibleNews, layer]);
  const shownKeysRef = useRef(shownKeys);
  shownKeysRef.current = shownKeys;

  const fitTo = (points: Point[], options: Record<string, unknown> = {}) => {
    const map = mapRef.current;
    if (!map || points.length === 0) return;
    const lngs = points.map((p) => p.longitude);
    const lats = points.map((p) => p.latitude);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 60, maxZoom: 12, ...options },
    );
  };

  // Build the map and one marker per pinned item once; filters and zoom decide what shows.
  useEffect(() => {
    if (!TOKEN || !containerRef.current) return;
    let disposed = false;
    const markers = markersRef.current;
    setUiLang(containerRef.current.closest(".eg-guide")?.getAttribute("lang") === "ar" ? "ar" : "en");
    (async () => {
      try {
        const mapboxgl = (await import("mapbox-gl")).default;
        if (disposed || !containerRef.current) return;
        mapboxgl.accessToken = TOKEN;
        popupClassRef.current = mapboxgl.Popup as unknown as PopupClass;
        // The region from the address, else the one this visitor last looked at, else Greater Cairo.
        let startSlug = region;
        if (startSlug === "all" && initialRegion === "all" && !lockRegion) {
          try {
            const remembered = window.localStorage.getItem(REGION_MEMORY_KEY);
            if (remembered && regions.some((r) => r.slug === remembered)) {
              startSlug = remembered;
              setRegion(remembered);
            }
          } catch {
            // No memory available.
          }
        }
        const start =
          regions.find((r) => r.slug === startSlug) ??
          regions.find((r) => r.slug === START_REGION) ??
          regions.find((r) => !r.parentSlug);
        const map = new mapboxgl.Map({
          container: containerRef.current,
          style: MAP_STYLES.streets,
          bounds: start?.bounds,
          fitBoundsOptions: { padding: 40 },
          attributionControl: true,
          cooperativeGestures: true,
        });
        (map as unknown as StylableMap).on("style.load", () => {
          if (map.getStyle()?.name?.toLowerCase().includes("light")) paintEgypt(map as unknown as StylableMap);
        });
        map.once("load", () => setMapReady(true));
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
        const geolocate = new mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: false,
          showUserLocation: false,
          fitBoundsOptions: { maxZoom: 13 },
        });
        map.addControl(geolocate, "top-right");
        let hereMarker: EngineMarker | null = null;
        geolocate.on("geolocate", (event) => {
          const { latitude, longitude } = (event as { coords: { latitude: number; longitude: number } }).coords;
          hereMarker?.remove();
          const el = document.createElement("div");
          el.className = "eg-marker--here";
          el.setAttribute("role", "img");
          el.setAttribute("aria-label", copy.youAreHere);
          hereMarker = new mapboxgl.Marker({ element: el }).setLngLat([longitude, latitude]).addTo(map) as unknown as EngineMarker;
          setHere({ latitude, longitude });
          setSort("nearest");
        });
        for (const item of mapItems) {
          const el = document.createElement("button");
          el.type = "button";
          el.className = `eg-marker eg-marker--${item.kind}${item.now ? " eg-marker--now" : ""}`;
          el.setAttribute("aria-label", item.label);
          el.setAttribute("aria-pressed", "false");
          const dot = document.createElement("span");
          dot.className = "eg-marker__dot";
          dot.textContent = item.glyph;
          el.appendChild(dot);
          el.addEventListener("click", (event) => {
            event.stopPropagation();
            setSelected((current) => (current === item.key ? null : item.key));
          });
          markers.set(item.key, new mapboxgl.Marker({ element: el }).setLngLat([item.longitude, item.latitude]).addTo(map) as unknown as EngineMarker);
        }
        mapRef.current = map;
        clustersRef.current = attachClusters({
          map: map as unknown as Parameters<typeof attachClusters>[0]["map"],
          Marker: mapboxgl.Marker as unknown as Parameters<typeof attachClusters>[0]["Marker"],
          markers,
          shown: () => shownKeysRef.current,
          label: (count) => `${count} ${copy.clusterOf}`,
        });
        spotlightRef.current = attachSpotlight(map as unknown as Parameters<typeof attachSpotlight>[0]);
        const startBounds = startSlug === "all" ? null : (regions.find((r) => r.slug === startSlug)?.bounds ?? null);
        if (startBounds) spotlightRef.current.set(startBounds);
      } catch {
        setMapFailed(true);
      }
    })();
    return () => {
      disposed = true;
      clustersRef.current?.dispose();
      clustersRef.current = null;
      spotlightRef.current?.dispose();
      spotlightRef.current = null;
      popupRef.current?.remove();
      popupRef.current = null;
      markers.forEach((m) => m.remove());
      markers.clear();
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // The items and regions are fixed for the page's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clustersRef.current) clustersRef.current.refresh();
    else markersRef.current.forEach((marker, key) => {
      marker.getElement().style.display = shownKeys.has(key) ? "" : "none";
    });
  }, [shownKeys]);

  // On a phone the map was hidden while the list showed; it needs to measure itself again.
  useEffect(() => {
    if (mobileView === "map") window.setTimeout(() => mapRef.current?.resize(), 50);
  }, [mobileView]);

  // The selected pin: pressed on the map, its card in view, and a callout over the pin.
  useEffect(() => {
    markersRef.current.forEach((marker, key) => {
      marker.getElement().setAttribute("aria-pressed", String(key === selected));
    });
    popupRef.current?.remove();
    popupRef.current = null;
    if (!selected) return;
    cardRefs.current.get(selected)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const map = mapRef.current;
    const Popup = popupClassRef.current;
    const item = itemByKey.get(selected);
    if (!map || !Popup || !item) return;
    const dir = uiLang === "ar" ? "rtl" : "ltr";
    const content = (() => {
      if (item.kind === "places") {
        const v = venues.find((x) => `p:${x.slug}` === selected);
        if (!v) return null;
        const open = openLabel(openOf.get(v.slug) ?? { known: false }, uiLang, now);
        return calloutElement({
          title: v.name,
          meta: [`${v.distinctionName} · ${v.category} · ${v.region}`, v.bestFor ?? "", open].filter(Boolean).join("\n"),
          image: v.coverUrl,
          href: venueHref[v.slug],
          linkLabel: copy.viewVenue,
          dir,
          extraLinks: [{ href: directionsHref(item), label: copy.directions, external: true }],
        });
      }
      if (item.kind === "events") {
        const e = events.find((x) => `e:${x.id}` === selected);
        if (!e) return null;
        return calloutElement({
          title: e.title,
          meta: [e.onNow ? copy.onNow : null, e.dates, e.where].filter(Boolean).join(" · "),
          href: e.href,
          linkLabel: e.external ? copy.officialSite : copy.viewVenue,
          external: e.external,
          dir,
          extraLinks: [{ href: directionsHref(item), label: copy.directions, external: true }],
        });
      }
      const n = news.find((x) => `n:${x.id}` === selected);
      if (!n) return null;
      return calloutElement({ title: n.title, meta: [n.month, n.source].filter(Boolean).join(" · "), href: n.href, linkLabel: n.external ? copy.readStory : copy.viewVenue, external: n.external, dir });
    })();
    if (!content) return;
    // Focus stays where the visitor was (the list, or the pin); the card is reachable from the list.
    popupRef.current = new Popup({ offset: 22, maxWidth: "none", closeButton: true, focusAfterOpen: false })
      .setLngLat([item.longitude, item.latitude])
      .setDOMContent(content)
      .addTo(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Escape clears the selection and its callout.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function chooseRegion(slug: string) {
    setRegion(slug);
    setSelected(null);
    if (!lockRegion) rememberRegion(slug);
    window.dispatchEvent(new CustomEvent(REGION_CHANGED_EVENT, { detail: { slug } }));
    const r = regions.find((x) => x.slug === slug);
    spotlightRef.current?.set(slug === "all" ? null : (r?.bounds ?? null));
    const map = mapRef.current;
    if (!map) return;
    if (slug === "all") {
      fitTo(mapItems);
      return;
    }
    if (r) map.fitBounds(r.bounds, { padding: 40 });
  }
  const chooseRegionRef = useRef(chooseRegion);
  chooseRegionRef.current = chooseRegion;

  // The drawn Egypt above sends its picks here.
  useEffect(() => {
    const onPick = (event: Event) => {
      const detail = (event as CustomEvent<RegionPickDetail>).detail;
      if (!regions.some((r) => r.slug === detail.slug)) return;
      detail.handled = true;
      chooseRegionRef.current(detail.slug);
    };
    window.addEventListener(REGION_PICK_EVENT, onPick);
    return () => window.removeEventListener(REGION_PICK_EVENT, onPick);
  }, [regions]);

  // The address follows the view, so it can be shared; the language and anything else stay.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const url = new URL(window.location.href);
    const set = (name: string, value: string | null) => (value ? url.searchParams.set(name, value) : url.searchParams.delete(name));
    if (!lockRegion) set("region", region === "all" ? null : region);
    set("show", layers.length > 1 && layer !== "all" ? layer : null);
    if (!lockCategory) set("category", category === "all" ? null : category);
    set("open", openOnly ? "1" : null);
    set("window", window_ === "all" ? null : window_);
    set("price", prices.size > 0 ? [...prices].sort().join(",") : null);
    set("min", minDistinction > 0 ? String(minDistinction) : null);
    set("tour", tourOnly ? "1" : null);
    set("sort", sort === "default" || sort === "nearest" ? null : sort);
    window.history.replaceState(window.history.state, "", url);
  }, [region, layer, category, layers.length, openOnly, window_, prices, minDistinction, tourOnly, sort, lockRegion, lockCategory]);

  function chooseStyle(next: MapStyleKey) {
    setStyle(next);
    mapRef.current?.setStyle(MAP_STYLES[next]);
  }

  async function shareView() {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ url, title: document.title });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {
      // The visitor closed the sheet, or nothing could be copied.
    }
  }

  const recentre = () => {
    const points = [...shownKeys].flatMap((key) => {
      const item = itemByKey.get(key);
      return item ? [item] : [];
    });
    fitTo(points.length > 0 ? points : mapItems, { maxZoom: 14 });
  };

  const filtersActive =
    (!lockCategory && category !== "all") || openOnly || window_ !== "all" || prices.size > 0 || minDistinction > 0 || tourOnly || sort === "name" || sort === "distinction";
  const clearFilters = () => {
    if (!lockCategory) setCategory("all");
    setOpenOnly(false);
    setWindow("all");
    setPrices(new Set());
    setMinDistinction(0);
    setTourOnly(false);
    setSort(here ? "nearest" : "default");
  };
  const togglePrice = (level: number) =>
    setPrices((current) => {
      const next = new Set(current);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });

  const cardRef = (key: string) => (el: HTMLLIElement | null) => {
    if (el) cardRefs.current.set(key, el);
    else cardRefs.current.delete(key);
  };
  const ring = (key: string) => (key === selected ? "0 0 0 2px var(--eg-gold)" : "0 0 0 1px var(--eg-line-soft)");
  const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const distance = (item: Pinned) =>
    here && isPinned(item) ? <span className="eg-distance">{formatDistance(distanceKm(here, item), uiLang)}</span> : null;
  const layerCounts: Record<ExplorerLayer, number> = {
    places: visibleVenues.length,
    events: visibleEvents.length + nationalEvents.length,
    news: visibleNews.length + nationalNews.length,
  };
  const layerLabel: Record<ExplorerLayer | "all", string> = {
    all: copy.layerAll,
    places: copy.layerPlaces,
    events: copy.layerEvents,
    news: copy.layerNews,
  };

  // Top-level regions as chips: the country's children, with what each holds in the current layer.
  const topRegions = regions.filter((r) => r.parentSlug === countrySlug || (r.parentSlug !== null && !regions.some((p) => p.slug === r.parentSlug)));
  const topOf = (slug: string | null): string | null => {
    let current = slug ? regions.find((r) => r.slug === slug) : undefined;
    while (current && current.parentSlug && current.parentSlug !== countrySlug) current = regions.find((r) => r.slug === current!.parentSlug);
    return current && current.parentSlug === countrySlug ? current.slug : null;
  };
  const activeTop = region === "all" ? null : topOf(region);
  const countInTop = (slug: string) => {
    const set = regionAndDescendants(slug, regions);
    let n = 0;
    if (shows("places")) n += venues.filter((v) => set.has(v.regionSlug) && (category === "all" || v.categorySlug === category)).length;
    if (shows("events")) n += events.filter((e) => e.regionSlug && set.has(e.regionSlug) && inWindow(e)).length;
    if (shows("news")) n += news.filter((x) => x.regionSlug && set.has(x.regionSlug)).length;
    return n;
  };
  // Category chips, grouped when the categories carry groups; each with its count in the chosen region.
  const countInCategory = (slug: string) => venues.filter((v) => v.categorySlug === slug && (regionFilter === null || regionFilter.has(v.regionSlug))).length;
  const groups = (["see", "do", "eat"] as const).map((g) => ({ key: g, label: copy.groups[g], items: categories.filter((c) => c.group === g) })).filter((g) => g.items.length > 0);
  const grouped = groups.length > 1;

  const eventCard = (e: ExplorerEvent) => {
    const key = `e:${e.id}`;
    const title = e.href ? (
      <a href={e.href} {...(e.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="eg-display" style={{ color: "var(--eg-ink)", fontSize: 19, lineHeight: 1.2, textDecoration: "none" }}>
        {e.title}
      </a>
    ) : (
      <span className="eg-display" style={{ fontSize: 19, lineHeight: 1.2 }}>
        {e.title}
      </span>
    );
    return (
      <li key={key} ref={cardRef(key)} style={{ ...ITEM_STYLE, boxShadow: ring(key) }} onMouseEnter={() => isPinned(e) && setSelected(key)}>
        <span
          aria-hidden="true"
          style={{ flex: "none", width: 64, display: "flex", flexDirection: "column", alignItems: "center", padding: "6px 0", borderRadius: 32, background: "var(--eg-lapis)", color: "var(--eg-gold-on-lapis)" }}
        >
          <span style={{ fontSize: 11, letterSpacing: "0.12em" }}>{e.mon}</span>
          <span className="eg-display" style={{ fontSize: 24, lineHeight: 1 }}>
            {e.day}
          </span>
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          {title}
          <span style={{ fontSize: 14, color: "var(--eg-muted)" }}>
            {e.onNow ? <strong style={{ color: "var(--eg-red)" }}>{copy.onNow} · </strong> : null}
            {e.dates}
            {e.where ? ` · ${e.where}` : ""}
            {e.region ? ` · ${e.region}` : ""}
            {distance(e) ? <> · {distance(e)}</> : null}
          </span>
        </div>
      </li>
    );
  };

  const newsCard = (n: ExplorerNews) => {
    const key = `n:${n.id}`;
    return (
      <li key={key} ref={cardRef(key)} style={{ ...ITEM_STYLE, boxShadow: ring(key), alignItems: "flex-start" }} onMouseEnter={() => isPinned(n) && setSelected(key)}>
        <span aria-hidden="true" className="eg-news-mark" />
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--eg-gold-text)" }}>{n.month}</span>
          {n.href ? (
            <a href={n.href} {...(n.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="eg-display" style={{ color: "var(--eg-ink)", fontSize: 18, lineHeight: 1.25, textDecoration: "none" }}>
              {n.title}
            </a>
          ) : (
            <span className="eg-display" style={{ fontSize: 18, lineHeight: 1.25 }}>
              {n.title}
            </span>
          )}
          <span style={{ fontSize: 14, color: "var(--eg-muted)" }}>
            {[n.source, n.region].filter(Boolean).join(" · ")}
            {distance(n) ? <> · {distance(n)}</> : null}
          </span>
        </div>
      </li>
    );
  };

  const placeCard = (v: ExplorerVenue) => {
    const key = `p:${v.slug}`;
    const open = openOf.get(v.slug);
    return (
      <li key={key} ref={cardRef(key)} style={{ ...ITEM_STYLE, boxShadow: ring(key) }} onMouseEnter={() => isPinned(v) && setSelected(key)}>
        <GuidePhoto src={v.coverUrl} alt="" label="" sizes="96px" style={{ width: 96, height: 76, objectFit: "cover", borderRadius: 2, flex: "none" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
          <span style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <Link href={venueHref[v.slug]} className="eg-display" style={{ color: "var(--eg-ink)", fontSize: 20, lineHeight: 1.2, textDecoration: "none" }}>
              {v.name}
            </Link>
            <GuideSaveButton slug={v.slug} copy={copy.saved} size="sm" />
          </span>
          <span style={{ fontSize: 14, color: "var(--eg-muted)", display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <DistinctionMark count={v.distinction} size={13} />
            {v.category} · {v.region}
            {v.priceLevel ? (
              <span aria-label={copy.priceBands[v.priceLevel - 1]} title={copy.priceBands[v.priceLevel - 1]} dir="ltr" style={{ color: "var(--eg-gold-text)", letterSpacing: "0.1em" }}>
                {"●".repeat(v.priceLevel)}
              </span>
            ) : null}
            {distance(v) ? <> · {distance(v)}</> : null}
          </span>
          {v.bestFor ? <span style={{ fontSize: 14, color: "var(--eg-text)" }}>{v.bestFor}</span> : null}
          <span style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            {open?.known ? (
              <span className={`eg-open ${open.open ? "eg-open--yes" : "eg-open--no"}`} title={openLabel(open, uiLang, now)}>
                {open.open ? copy.openNow : copy.closedNow}
              </span>
            ) : null}
            {v.hasTour ? (
              <Link href={`${venueHref[v.slug]}#tour`} style={{ fontSize: 14, fontWeight: 600 }}>
                {copy.tourShort} →
              </Link>
            ) : null}
          </span>
        </div>
      </li>
    );
  };

  const groupHead = (key: string, text: string, note?: string) => (
    <li key={key} style={{ padding: "8px 2px 0", display: "flex", flexDirection: "column", gap: 2 }}>
      <span className="eg-eyebrow">{text}</span>
      {note ? <span style={{ fontSize: 13, color: "var(--eg-muted)" }}>{note}</span> : null}
    </li>
  );

  const sections: React.ReactNode[] = [];
  if (shows("places")) {
    if (layer === "all") sections.push(groupHead("h-p", `${copy.layerPlaces} · ${visibleVenues.length}`));
    if (visibleVenues.length === 0 && layer !== "all") sections.push(<li key="x-p" style={{ ...ITEM_STYLE, padding: 16 }}>{copy.empty}</li>);
    sections.push(...visibleVenues.map(placeCard));
  }
  if (shows("events")) {
    if (layer === "all" && visibleEvents.length > 0) sections.push(groupHead("h-e", `${copy.layerEvents} · ${visibleEvents.length}`));
    if (visibleEvents.length === 0 && layer !== "all") sections.push(<li key="x-e" style={{ ...ITEM_STYLE, padding: 16 }}>{copy.emptyEvents}</li>);
    sections.push(...visibleEvents.map(eventCard));
  }
  if (shows("news")) {
    if (layer === "all" && visibleNews.length > 0) sections.push(groupHead("h-n", `${copy.layerNews} · ${visibleNews.length}`));
    if (visibleNews.length === 0 && layer !== "all") sections.push(<li key="x-n" style={{ ...ITEM_STYLE, padding: 16 }}>{copy.emptyNews}</li>);
    sections.push(...visibleNews.map(newsCard));
  }
  // In the everything view, a taste of the nationwide items; one kind's view lists them all.
  const cap = <T,>(items: T[]) => (layer === "all" ? items.slice(0, 4) : items);
  const nationwide = [
    ...(shows("events") ? cap(nationalEvents).map(eventCard) : []),
    ...(shows("news") ? cap(nationalNews).map(newsCard) : []),
  ];
  if (nationwide.length > 0) sections.push(groupHead("h-x", copy.acrossEgypt, copy.acrossEgyptNote), ...nationwide);

  const total = (shows("places") ? visibleVenues.length : 0) + (shows("events") ? layerCounts.events : 0) + (shows("news") ? layerCounts.news : 0);
  const summary =
    layer === "places"
      ? count(visibleVenues.length, copy.place, copy.places)
      : layer === "events"
        ? count(layerCounts.events, copy.event, copy.events)
        : layer === "news"
          ? count(layerCounts.news, copy.story, copy.stories)
          : [
              shows("places") ? count(visibleVenues.length, copy.place, copy.places) : null,
              shows("events") ? count(layerCounts.events, copy.event, copy.events) : null,
              shows("news") ? count(layerCounts.news, copy.story, copy.stories) : null,
            ]
              .filter(Boolean)
              .join(" · ");

  const chip = (c: ExplorerCategory) => (
    <button key={c.slug} type="button" className="eg-chip" aria-pressed={category === c.slug} onClick={() => setCategory((current) => (current === c.slug ? "all" : c.slug))}>
      {c.label}
      <span className="eg-chip__count">{countInCategory(c.slug)}</span>
    </button>
  );

  return (
    <div className="eg-explorer" data-view={mobileView} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="eg-panel" style={{ padding: 12, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {layers.length > 1 ? (
          <div role="group" aria-label={copy.layerAll} className="eg-segmented">
            {(["all", ...layers] as Array<ExplorerLayer | "all">).map((l) => (
              <button key={l} type="button" aria-pressed={layer === l} onClick={() => setLayer(l)}>
                {l !== "all" ? <span aria-hidden="true" className={`eg-layer-key eg-layer-key--${l}`} /> : null}
                {layerLabel[l]}
                {l !== "all" ? <span className="eg-segmented__count">{layerCounts[l]}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        <span style={{ flex: "1 1 16px" }} />
        {!lockRegion ? (
          <>
            <label htmlFor="eg-region" style={{ color: "var(--eg-muted)", fontWeight: 600 }}>
              {copy.region}
            </label>
            <select id="eg-region" className="eg-select" value={region} onChange={(e) => chooseRegion(e.target.value)}>
              <option value="all">{copy.allRegions}</option>
              {regions
                .filter((r) => r.parentSlug)
                .map((r) => (
                  <option key={r.slug} value={r.slug}>
                    {r.parentSlug && regions.find((p) => p.slug === r.parentSlug)?.parentSlug ? `— ${r.label}` : r.label}
                  </option>
                ))}
            </select>
          </>
        ) : null}
        {shows("places") ? (
          <>
            <label htmlFor="eg-sort" style={{ color: "var(--eg-muted)", fontWeight: 600 }}>
              {copy.filters.sort}
            </label>
            <select id="eg-sort" className="eg-select" value={sort} onChange={(e) => setSort(e.target.value as ExplorerSort)}>
              <option value="default">{copy.filters.sortDefault}</option>
              <option value="name">{copy.filters.sortName}</option>
              <option value="distinction">{copy.filters.sortDistinction}</option>
              {here ? <option value="nearest">{copy.filters.sortNearest}</option> : null}
            </select>
          </>
        ) : null}
        {!lockRegion && topRegions.length > 1 ? (
          <div role="group" aria-label={copy.region} className="eg-region-chips">
            <button type="button" className="eg-chip" aria-pressed={region === "all"} onClick={() => chooseRegion("all")}>
              {copy.allRegions}
            </button>
            {topRegions.map((r) => (
              <button key={r.slug} type="button" className="eg-chip" aria-pressed={activeTop === r.slug} onClick={() => chooseRegion(r.slug)}>
                {r.label}
                <span className="eg-chip__count">{countInTop(r.slug)}</span>
              </button>
            ))}
          </div>
        ) : null}
        {shows("places") && categories.length > 0 && !lockCategory ? (
          <div role="group" aria-label={copy.category} style={{ display: "flex", flexWrap: "wrap", gap: "8px 14px", flexBasis: "100%", alignItems: "center" }}>
            <button type="button" className="eg-chip" aria-pressed={category === "all"} onClick={() => setCategory("all")}>
              {copy.all}
            </button>
            {grouped
              ? groups.map((g) => (
                  <span key={g.key} className="eg-chip-group">
                    <span className="eg-chip-group__label">{g.label}</span>
                    {g.items.map(chip)}
                  </span>
                ))
              : categories.map(chip)}
          </div>
        ) : null}
        {shows("places") ? (
          <div role="group" aria-label={copy.filters.more} style={{ display: "flex", flexWrap: "wrap", gap: 8, flexBasis: "100%", alignItems: "center" }}>
            {venues.some((v) => openOf.get(v.slug)?.known) ? (
              <button type="button" className="eg-chip" aria-pressed={openOnly} onClick={() => setOpenOnly((v) => !v)} style={{ borderStyle: "dashed" }}>
                {copy.filterOpenNow}
              </button>
            ) : null}
            {venues.some((v) => v.hasTour) ? (
              <button type="button" className="eg-chip" aria-pressed={tourOnly} onClick={() => setTourOnly((v) => !v)} style={{ borderStyle: "dashed" }}>
                {copy.filters.tour}
              </button>
            ) : null}
            <button type="button" className="eg-chip" aria-pressed={moreFilters} aria-expanded={moreFilters} onClick={() => setMoreFilters((v) => !v)} style={{ borderStyle: "dashed" }}>
              {copy.filters.more}
            </button>
            {filtersActive ? (
              <button type="button" className="eg-mini-button" onClick={clearFilters}>
                {copy.filters.clear}
              </button>
            ) : null}
            {moreFilters ? (
              <div style={{ flexBasis: "100%", display: "flex", flexWrap: "wrap", gap: "8px 24px", alignItems: "center" }}>
                {venues.some((v) => v.priceLevel) ? (
                  <span role="group" aria-label={copy.filters.price} style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    <span className="eg-eyebrow" style={{ marginInlineEnd: 4 }}>
                      {copy.filters.price}
                    </span>
                    {PRICE_LEVELS.map((level) => (
                      <button key={level} type="button" className="eg-chip" aria-pressed={prices.has(level)} onClick={() => togglePrice(level)} title={copy.priceBands[level - 1]} dir="ltr" style={{ minHeight: 36, letterSpacing: "0.08em" }}>
                        {"●".repeat(level)}
                      </button>
                    ))}
                  </span>
                ) : null}
                <span role="group" aria-label={copy.filters.distinction} style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                  <span className="eg-eyebrow" style={{ marginInlineEnd: 4 }}>
                    {copy.filters.distinction}
                  </span>
                  <button type="button" className="eg-chip" aria-pressed={minDistinction === 0} onClick={() => setMinDistinction(0)} style={{ minHeight: 36 }}>
                    {copy.filters.any}
                  </button>
                  {[1, 2, 3].map((level) => (
                    <button key={level} type="button" className="eg-chip" aria-pressed={minDistinction === level} onClick={() => setMinDistinction(level)} style={{ minHeight: 36 }}>
                      <DistinctionMark count={level} size={12} />+
                    </button>
                  ))}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
        {shows("events") && events.some((e) => e.startsAt) ? (
          <div role="group" aria-label={copy.layerEvents} style={{ display: "flex", flexWrap: "wrap", gap: 8, flexBasis: "100%" }}>
            {(["all", "now", "week", "month"] as EventWindow[]).map((w) => (
              <button key={w} type="button" className="eg-chip" aria-pressed={window_ === w} onClick={() => setWindow(w)} style={{ minHeight: 38, fontSize: 14 }}>
                {copy.eventWindows[w]}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {!mapFailed ? (
        <div role="group" aria-label={`${copy.filters.showMap} / ${copy.filters.showList}`} className="eg-segmented eg-explorer__toggle">
          <button type="button" aria-pressed={mobileView === "list"} onClick={() => setMobileView("list")}>
            {copy.filters.showList}
            <span className="eg-segmented__count">{total}</span>
          </button>
          <button type="button" aria-pressed={mobileView === "map"} onClick={() => setMobileView("map")}>
            {copy.filters.showMap}
          </button>
        </div>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        <div className="eg-explorer__map" style={{ flex: "999 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {mapFailed ? (
            <p className="eg-panel" style={{ margin: 0, padding: 16, color: "var(--eg-muted)" }}>
              {copy.mapUnavailable}
            </p>
          ) : (
            <>
              <div className="eg-frame">
                <div className="eg-frame__inner eg-map-shell" style={{ height: "min(70vh, 620px)", minHeight: 380 }}>
                  <div dir="ltr" ref={containerRef} style={{ position: "absolute", inset: 0 }} />
                  {!mapReady ? (
                    <div className="eg-map-skeleton" aria-live="polite">
                      {copy.loadingMap}
                    </div>
                  ) : null}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <ul className="eg-legend" aria-label={copy.legendHint}>
                  {layers.includes("places") ? (
                    <li>
                      <span aria-hidden="true" className="eg-layer-key" /> {copy.layerPlaces}
                    </li>
                  ) : null}
                  {layers.includes("events") ? (
                    <li>
                      <span aria-hidden="true" className="eg-layer-key eg-layer-key--events" /> {copy.layerEvents}
                    </li>
                  ) : null}
                  {layers.includes("news") ? (
                    <li>
                      <span aria-hidden="true" className="eg-layer-key eg-layer-key--news" /> {copy.layerNews}
                    </li>
                  ) : null}
                  <li>
                    <span aria-hidden="true" className="eg-legend__cluster">
                      9
                    </span>{" "}
                    {copy.legendCluster}
                  </li>
                  <li>
                    <span aria-hidden="true" className="eg-legend__here" /> {copy.legendHere}
                  </li>
                </ul>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  <div role="group" aria-label={`${copy.styleStreets} / ${copy.styleSatellite}`} className="eg-mini-group">
                    <button type="button" className="eg-mini-button" aria-pressed={style === "streets"} onClick={() => chooseStyle("streets")}>
                      {copy.styleStreets}
                    </button>
                    <button type="button" className="eg-mini-button" aria-pressed={style === "satellite"} onClick={() => chooseStyle("satellite")}>
                      {copy.styleSatellite}
                    </button>
                  </div>
                  {here ? (
                    <button type="button" className="eg-mini-button" aria-pressed={nearestFirst} onClick={() => setSort((s) => (s === "nearest" ? "default" : "nearest"))}>
                      {copy.nearestFirst}
                    </button>
                  ) : null}
                  <button type="button" className="eg-mini-button" onClick={recentre}>
                    {copy.recentre}
                  </button>
                  <button type="button" className="eg-mini-button" onClick={shareView} aria-live="polite" data-eg-click="share" data-eg-subject-type="page" data-eg-subject="map">
                    {shared ? copy.linkCopied : copy.shareView}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="eg-explorer__list" style={{ flex: "1 1 340px", minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <p role="status" style={{ margin: 0, color: "var(--eg-muted)" }}>
            {summary}
          </p>
          {total === 0 ? (
            <p className="eg-panel" style={{ margin: 0, padding: 16 }}>
              {layer === "events" ? copy.emptyEvents : layer === "news" ? copy.emptyNews : copy.empty}
              {filtersActive ? (
                <>
                  {" "}
                  <button type="button" className="eg-mini-button" onClick={clearFilters}>
                    {copy.filters.clear}
                  </button>
                </>
              ) : null}
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10, maxHeight: "min(70vh, 620px)", overflowY: "auto" }}>
              {sections}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
